// src/hooks/useContract.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  BrowserProvider,
  Contract,
  JsonRpcProvider,
  formatUnits,
  parseUnits,
} from "ethers";

import {
  useAppKitAccount,
  useAppKitProvider,
  useAppKitNetwork,
} from "@reown/appkit/react";

import {
  KIND_RUSH_ABI,
  TOKEN_ABI,
} from "../contracts";

/* =========================================================
   CONFIG
========================================================= */

const CONTRACT_ADDRESS ="0x900ADA53B342ebdF097465cefDD65Ac13Cb928f6";

const RPC_URL ="https://bsc-dataseed.bnbchain.org";

/*
 * Default values from your Solidity contract.
 *
 * These are ONLY frontend constants.
 * Contract remains the source of truth.
 */
const DEFAULT_USD_DECIMALS = 18;
const DEFAULT_KIND_DECIMALS = 18;

/* =========================================================
   ENUMS - EXACTLY MATCH SOLIDITY
========================================================= */

export const WALLET_STATUS = {
  NOT_REGISTERED: 0,
  QUALIFYING: 1,
  ACTIVE: 2,
  COMPLETED: 3,
  DISQUALIFIED: 4,
};

export const DISQUALIFICATION_REASON = {
  NONE: 0,
  KIND_SOLD: 1,
  KIND_SENT: 2,
  KIND_RECEIVED: 3,
  INVALID_TRADE: 4,
  UNAUTHORIZED_CONTRACT: 5,
  ADMIN_EMERGENCY: 6,
};

/* =========================================================
   HELPERS
========================================================= */

function normalizeError(error) {
  if (!error) {
    return "Unknown error";
  }

  if (typeof error === "string") {
    return error;
  }

  if (error.reason) {
    return error.reason;
  }

  if (error.shortMessage) {
    return error.shortMessage;
  }

  if (error.message) {
    return error.message;
  }

  return "Transaction failed";
}

function formatTokenAmount(value, decimals = 18) {
  if (
    value === undefined ||
    value === null
  ) {
    return "0";
  }

  try {
    return formatUnits(value, decimals);
  } catch {
    return "0";
  }
}

function formatUsd(value) {
  return formatTokenAmount(
    value,
    DEFAULT_USD_DECIMALS
  );
}

function toNumber(value) {
  if (
    value === undefined ||
    value === null
  ) {
    return 0;
  }

  try {
    return Number(value.toString());
  } catch {
    return 0;
  }
}

function normalizeAddress(address) {
  if (!address) {
    return "";
  }

  return address;
}

function ensureAddress(address, name = "address") {
  if (!address) {
    throw new Error(
      `${name} is required`
    );
  }

  return address;
}

/*
 * Convert a normal decimal string into
 * Solidity uint256 using ethers v6.
 *
 * Example:
 * "200" -> 200e18
 */
function usdToWei(value) {
  return parseUnits(
    String(value),
    DEFAULT_USD_DECIMALS
  );
}

/*
 * Convert token amount using token decimals.
 */
function tokenToUnits(
  value,
  decimals = DEFAULT_KIND_DECIMALS
) {
  return parseUnits(
    String(value),
    decimals
  );
}

/* =========================================================
   HOOK
========================================================= */

export default function useContract() {
  const {
    address: account,
    isConnected,
  } = useAppKitAccount();

  useEffect(() => {
  let mounted = true;

  const setupProvider = async () => {
    try {
      // ==============================
      // READ-ONLY PROVIDER
      // ==============================
      if (RPC_URL) {
        const rpcProvider = new JsonRpcProvider(RPC_URL);

        if (mounted) {
          setReadContract(
            new Contract(
              CONTRACT_ADDRESS,
              KIND_RUSH_ABI,
              rpcProvider
            )
          );
        }
      }

      // ==============================
      // APPKIT WALLET PROVIDER
      // ==============================
      if (!walletProvider || !isConnected) {
        if (mounted) {
          setProvider(null);
          setSigner(null);
          setContract(null);
        }
        return;
      }

      // IMPORTANT:
      // This provider comes from AppKit.
      // It supports Bitget / MetaMask / WalletConnect
      // without directly using window.ethereum.
      const browserProvider = new BrowserProvider(
        walletProvider
      );

      // ==============================
      // CHECK CONNECTED NETWORK
      // ==============================
      const network = await browserProvider.getNetwork();

      const connectedChainId = Number(network.chainId);

      console.log(
        "AppKit connected chain:",
        connectedChainId
      );

      // KTL is deployed on BSC Mainnet
      if (connectedChainId !== 56) {
        console.warn(
          `Wrong network. Connected chain: ${connectedChainId}. Expected BSC Mainnet (56).`
        );

        if (mounted) {
          setProvider(browserProvider);
          setSigner(null);
          setContract(null);
        }

        return;
      }

      // ==============================
      // GET APPKIT SIGNER
      // ==============================
      const walletSigner =
        await browserProvider.getSigner();

      // ==============================
      // CONTRACT
      // ==============================
      const walletContract = new Contract(
        CONTRACT_ADDRESS,
        KIND_RUSH_ABI,
        walletSigner
      );

      if (mounted) {
        setProvider(browserProvider);
        setSigner(walletSigner);
        setContract(walletContract);

        console.log(
          "AppKit wallet provider connected successfully"
        );
        console.log(
          "BSC signer:",
          await walletSigner.getAddress()
        );
      }
    } catch (error) {
      console.error(
        "AppKit provider setup failed:",
        error
      );

      if (mounted) {
        setProvider(null);
        setSigner(null);
        setContract(null);
      }
    }
  };

  setupProvider();

  return () => {
    mounted = false;
  };
}, [walletProvider, isConnected]);


  const {
    walletProvider,
  } = useAppKitProvider("eip155");

  const network = useAppKitNetwork();

  const [provider, setProvider] =
    useState(null);

  const [signer, setSigner] =
    useState(null);

  const [contract, setContract] =
    useState(null);

  const [readContract, setReadContract] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState(null);

  const [lastTxHash, setLastTxHash] =
    useState("");

  /* =======================================================
     NETWORK / PROVIDER
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    async function setupProvider() {
      try {
        /*
         * Read-only RPC provider.
         */
        if (RPC_URL) {
          const rpcProvider =
            new JsonRpcProvider(
              RPC_URL
            );

          if (mounted) {
            setReadContract(
              new Contract(
                CONTRACT_ADDRESS,
                KIND_RUSH_ABI,
                rpcProvider
              )
            );
          }
        }

        /*
         * Connected wallet provider.
         */
        if (
          walletProvider &&
          isConnected
        ) {
          const browserProvider =
            new BrowserProvider(
              walletProvider
            );

          if (mounted) {
            setProvider(
              browserProvider
            );
          }

          const walletSigner =
            await browserProvider.getSigner();

          if (mounted) {
            setSigner(
              walletSigner
            );

            setContract(
              new Contract(
                CONTRACT_ADDRESS,
                KIND_RUSH_ABI,
                walletSigner
              )
            );

            /*
             * If no RPC URL exists, use wallet
             * provider for reads too.
             */
            if (!RPC_URL) {
              setReadContract(
                new Contract(
                  CONTRACT_ADDRESS,
                  KIND_RUSH_ABI,
                  browserProvider
                )
              );
            }
          }
        } else {
          if (mounted) {
            setProvider(null);
            setSigner(null);
            setContract(null);
          }
        }
      } catch (err) {
        if (mounted) {
          setError(
            normalizeError(err)
          );
        }
      }
    }

    setupProvider();

    return () => {
      mounted = false;
    };
  }, [
    walletProvider,
    isConnected,
  ]);

  /* =======================================================
     NETWORK
  ======================================================= */

  const chainId = useMemo(() => {
    if (!network) {
      return null;
    }

    if (
      network.chainId !== undefined &&
      network.chainId !== null
    ) {
      return network.chainId;
    }

    return null;
  }, [network]);

  /* =======================================================
     GENERIC READ
  ======================================================= */

  const executeRead = useCallback(
    async (callback) => {
      setError(null);

      try {
        if (!readContract) {
          throw new Error(
            "Read contract is not ready"
          );
        }

        return await callback(
          readContract
        );
      } catch (err) {
        const message =
          normalizeError(err);

        setError(message);

        throw new Error(message);
      }
    },
    [readContract]
  );

  /* =======================================================
     GENERIC WRITE
  ======================================================= */

  const executeWrite = useCallback(
    async (callback) => {
      setLoading(true);
      setError(null);
      setLastTxHash("");

      try {
        if (!contract) {
          throw new Error(
            "Wallet is not connected"
          );
        }

        const tx =
          await callback(contract);

        if (tx?.hash) {
          setLastTxHash(tx.hash);
        }

        const receipt =
          await tx.wait();

        if (receipt?.hash) {
          setLastTxHash(
            receipt.hash
          );
        }

        return receipt;
      } catch (err) {
        const message =
          normalizeError(err);

        setError(message);

        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [contract]
  );

  /* =======================================================
     CONTRACT CONSTANTS
     
     These are Solidity public constants,
     so they are generated as contract functions.
  ======================================================= */

  const getBps = useCallback(
    () =>
      executeRead((c) =>
        c.BPS()
      ),
    [executeRead]
  );

  const getUsdOne = useCallback(
    () =>
      executeRead((c) =>
        c.USD_ONE()
      ),
    [executeRead]
  );

  const getClaimCooldown = useCallback(
    () =>
      executeRead((c) =>
        c.CLAIM_COOLDOWN()
      ),
    [executeRead]
  );

  const getDefaultQualificationPeriod =
    useCallback(
      () =>
        executeRead((c) =>
          c.DEFAULT_QUALIFICATION_PERIOD()
        ),
      [executeRead]
    );

  const getDefaultMaxPurchaseUsd =
    useCallback(
      () =>
        executeRead((c) =>
          c.DEFAULT_MAX_PURCHASE_USD()
        ),
      [executeRead]
    );

  const getMaxClaims = useCallback(
    () =>
      executeRead((c) =>
        c.MAX_CLAIMS()
      ),
    [executeRead]
  );

  const getReward100 = useCallback(
    () =>
      executeRead((c) =>
        c.REWARD_100()
      ),
    [executeRead]
  );

  const getReward150 = useCallback(
    () =>
      executeRead((c) =>
        c.REWARD_150()
      ),
    [executeRead]
  );

  const getReward200 = useCallback(
    () =>
      executeRead((c) =>
        c.REWARD_200()
      ),
    [executeRead]
  );

  const getReward300 = useCallback(
    () =>
      executeRead((c) =>
        c.REWARD_300()
      ),
    [executeRead]
  );

  const getNextCampaignId =
    useCallback(
      () =>
        executeRead((c) =>
          c.nextCampaignId()
        ),
      [executeRead]
    );

  const getDefaultKindToken =
    useCallback(
      () =>
        executeRead((c) =>
          c.defaultKindToken()
        ),
      [executeRead]
    );

  /* =======================================================
     ADMIN READS
     
     Exact Solidity functions:
     approvedKindPairs(address)
     authorizedOperators(address)
     recordedTrades(bytes32)
  ======================================================= */

  const isApprovedKindPair =
    useCallback(
      async (pair) =>
        executeRead((c) =>
          c.approvedKindPairs(
            ensureAddress(
              pair,
              "pair"
            )
          )
        ),
      [executeRead]
    );

  const isAuthorizedOperator =
    useCallback(
      async (operator) =>
        executeRead((c) =>
          c.authorizedOperators(
            ensureAddress(
              operator,
              "operator"
            )
          )
        ),
      [executeRead]
    );

  const isTradeRecorded =
    useCallback(
      async (tradeId) =>
        executeRead((c) =>
          c.recordedTrades(
            tradeId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     setOperator
     
     Solidity:
     setOperator(
         address operator,
         bool approved
     )
  ======================================================= */

  const setOperator = useCallback(
    async (
      operator,
      approved
    ) =>
      executeWrite((c) =>
        c.setOperator(
          ensureAddress(
            operator,
            "operator"
          ),
          Boolean(approved)
        )
      ),
    [executeWrite]
  );

  /* =======================================================
     setApprovedKindPair
  ======================================================= */

  const setApprovedKindPair =
    useCallback(
      async (
        pair,
        approved
      ) =>
        executeWrite((c) =>
          c.setApprovedKindPair(
            ensureAddress(
              pair,
              "pair"
            ),
            Boolean(approved)
          )
        ),
      [executeWrite]
    );

  /* =======================================================
     createCampaign
     
     Solidity EXACT ORDER:
     
     createCampaign(
       startTime,
       endTime,
       claimEndTime,
       qualificationPeriod,
       minPurchaseUsd,
       maxPurchaseUsd,
       claimCount,
       usdtToken,
       wbnbToken,
       kindWbnbPair,
       wbnbUsdtPair
     )
  ======================================================= */

  const createCampaign =
    useCallback(
      async ({
        startTime,
        endTime,
        claimEndTime,
        qualificationPeriod,
        minPurchaseUsd,
        maxPurchaseUsd,
        claimCount,
        usdtToken,
        wbnbToken,
        kindWbnbPair,
        wbnbUsdtPair,
      }) =>
        executeWrite((c) =>
          c.createCampaign(
            startTime,
            endTime,
            claimEndTime,
            qualificationPeriod,
            minPurchaseUsd,
            maxPurchaseUsd,
            claimCount,
            ensureAddress(
              usdtToken,
              "usdtToken"
            ),
            ensureAddress(
              wbnbToken,
              "wbnbToken"
            ),
            ensureAddress(
              kindWbnbPair,
              "kindWbnbPair"
            ),
            ensureAddress(
              wbnbUsdtPair,
              "wbnbUsdtPair"
            )
          )
        ),
      [executeWrite]
    );

  /*
   * Convenience version for USD decimal strings.
   *
   * This still calls the EXACT Solidity function.
   */
  const createCampaignFromValues =
    useCallback(
      async ({
        startTime,
        endTime,
        claimEndTime,
        qualificationPeriod,
        minPurchaseUsd,
        maxPurchaseUsd,
        claimCount,
        usdtToken,
        wbnbToken,
        kindWbnbPair,
        wbnbUsdtPair,
      }) =>
        createCampaign({
          startTime,
          endTime,
          claimEndTime,
          qualificationPeriod,
          minPurchaseUsd:
            usdToWei(
              minPurchaseUsd
            ),
          maxPurchaseUsd:
            usdToWei(
              maxPurchaseUsd
            ),
          claimCount,
          usdtToken,
          wbnbToken,
          kindWbnbPair,
          wbnbUsdtPair,
        }),
      [createCampaign]
    );

  /* =======================================================
     activateCampaign
  ======================================================= */

  const activateCampaign =
    useCallback(
      async (campaignId) =>
        executeWrite((c) =>
          c.activateCampaign(
            campaignId
          )
        ),
      [executeWrite]
    );

  /* =======================================================
     recordVerifiedBuy
     
     Solidity EXACT ORDER:
     
     recordVerifiedBuy(
       campaignId,
       wallet,
       tradeId,
       pair,
       kindAmount,
       usdValue,
       tradeTimestamp
     )
  ======================================================= */

  const recordVerifiedBuy =
    useCallback(
      async ({
        campaignId,
        wallet,
        tradeId,
        pair,
        kindAmount,
        usdValue,
        tradeTimestamp,
      }) =>
        executeWrite((c) =>
          c.recordVerifiedBuy(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            ),
            tradeId,
            ensureAddress(
              pair,
              "pair"
            ),
            kindAmount,
            usdValue,
            tradeTimestamp
          )
        ),
      [executeWrite]
    );

  /*
   * Convenience version for normal decimal
   * KIND/USD values.
   */
  const recordVerifiedBuyFromValues =
    useCallback(
      async ({
        campaignId,
        wallet,
        tradeId,
        pair,
        kindAmount,
        kindDecimals = DEFAULT_KIND_DECIMALS,
        usdValue,
        tradeTimestamp,
      }) =>
        recordVerifiedBuy({
          campaignId,
          wallet,
          tradeId,
          pair,
          kindAmount:
            tokenToUnits(
              kindAmount,
              kindDecimals
            ),
          usdValue:
            usdToWei(
              usdValue
            ),
          tradeTimestamp,
        }),
      [recordVerifiedBuy]
    );

  /* =======================================================
     getWbnbUsdPrice
  ======================================================= */

  const getWbnbUsdPrice =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getWbnbUsdPrice(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getKindUsdPrice
  ======================================================= */

  const getKindUsdPrice =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getKindUsdPrice(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     claim
  ======================================================= */

  const claim = useCallback(
    async (campaignId) =>
      executeWrite((c) =>
        c.claim(
          campaignId
        )
      ),
    [executeWrite]
  );

  /* =======================================================
     fundRewardContract
     
     Solidity:
     fundRewardContract(
       campaignId,
       kindAmount
     )
  ======================================================= */

  const fundRewardContract =
    useCallback(
      async (
        campaignId,
        kindAmount
      ) =>
        executeWrite((c) =>
          c.fundRewardContract(
            campaignId,
            kindAmount
          )
        ),
      [executeWrite]
    );

  const fundRewardContractFromValue =
    useCallback(
      async (
        campaignId,
        kindAmount,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) =>
        fundRewardContract(
          campaignId,
          tokenToUnits(
            kindAmount,
            kindDecimals
          )
        ),
      [fundRewardContract]
    );

  /* =======================================================
     disqualifyWallet
     
     Solidity:
     disqualifyWallet(
       campaignId,
       wallet,
       reason
     )
  ======================================================= */

  const disqualifyWallet =
    useCallback(
      async (
        campaignId,
        wallet,
        reason
      ) =>
        executeWrite((c) =>
          c.disqualifyWallet(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            ),
            reason
          )
        ),
      [executeWrite]
    );

  /* =======================================================
     emergencyDisqualify
  ======================================================= */

  const emergencyDisqualify =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeWrite((c) =>
          c.emergencyDisqualify(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeWrite]
    );

  /* =======================================================
     pause
  ======================================================= */

  const pause = useCallback(
    async () =>
      executeWrite((c) =>
        c.pause()
      ),
    [executeWrite]
  );

  /* =======================================================
     unpause
  ======================================================= */

  const unpause = useCallback(
    async () =>
      executeWrite((c) =>
        c.unpause()
      ),
    [executeWrite]
  );

  /* =======================================================
     closeCampaign
  ======================================================= */

  const closeCampaign =
    useCallback(
      async (campaignId) =>
        executeWrite((c) =>
          c.closeCampaign(
            campaignId
          )
        ),
      [executeWrite]
    );

  /* =======================================================
     withdrawExcessKind
     
     Solidity:
     withdrawExcessKind(
       campaignId,
       receiver,
       amount
     )
  ======================================================= */

  const withdrawExcessKind =
    useCallback(
      async (
        campaignId,
        receiver,
        amount
      ) =>
        executeWrite((c) =>
          c.withdrawExcessKind(
            campaignId,
            ensureAddress(
              receiver,
              "receiver"
            ),
            amount
          )
        ),
      [executeWrite]
    );

  const withdrawExcessKindFromValue =
    useCallback(
      async (
        campaignId,
        receiver,
        amount,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) =>
        withdrawExcessKind(
          campaignId,
          receiver,
          tokenToUnits(
            amount,
            kindDecimals
          )
        ),
      [withdrawExcessKind]
    );

  /* =======================================================
     withdrawERC20
  ======================================================= */

  const withdrawERC20 =
    useCallback(
      async (
        token,
        receiver,
        amount
      ) =>
        executeWrite((c) =>
          c.withdrawERC20(
            ensureAddress(
              token,
              "token"
            ),
            ensureAddress(
              receiver,
              "receiver"
            ),
            amount
          )
        ),
      [executeWrite]
    );

  const withdrawERC20FromValue =
    useCallback(
      async (
        token,
        receiver,
        amount,
        decimals = DEFAULT_KIND_DECIMALS
      ) =>
        withdrawERC20(
          token,
          receiver,
          tokenToUnits(
            amount,
            decimals
          )
        ),
      [withdrawERC20]
    );

  /* =======================================================
     getUserStats
  ======================================================= */

  const getUserStats =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getUserStats(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getCampaign
  ======================================================= */

  const getCampaign =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getCampaign(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getParticipant
  ======================================================= */

  const getParticipant =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getParticipant(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getRewardBpsForAmount
  ======================================================= */

  const getRewardBpsForAmount =
    useCallback(
      async (qualifyingUsd) =>
        executeRead((c) =>
          c.getRewardBpsForAmount(
            qualifyingUsd
          )
        ),
      [executeRead]
    );

  const getRewardBpsForUsd =
    useCallback(
      async (qualifyingUsd) =>
        getRewardBpsForAmount(
          usdToWei(
            qualifyingUsd
          )
        ),
      [getRewardBpsForAmount]
    );

  /* =======================================================
     getNextClaimUsd
  ======================================================= */

  const getNextClaimUsd =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getNextClaimUsd(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getNextClaimKind
  ======================================================= */

  const getNextClaimKind =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getNextClaimKind(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getClaimInfo
  ======================================================= */

  const getClaimInfo =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getClaimInfo(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     isClaimReady
  ======================================================= */

  const isClaimReady =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.isClaimReady(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getTimeUntilNextClaim
  ======================================================= */

  const getTimeUntilNextClaim =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getTimeUntilNextClaim(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getTrade
  ======================================================= */

  const getTrade =
    useCallback(
      async (
        campaignId,
        wallet,
        tradeId
      ) =>
        executeRead((c) =>
          c.getTrade(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            ),
            tradeId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getTradeCount
  ======================================================= */

  const getTradeCount =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getTradeCount(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getWalletStatus
  ======================================================= */

  const getWalletStatus =
    useCallback(
      async (
        campaignId,
        wallet
      ) =>
        executeRead((c) =>
          c.getWalletStatus(
            campaignId,
            ensureAddress(
              wallet,
              "wallet"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     hasParticipated
     
     Solidity argument order:
     hasParticipated(
       address wallet,
       uint256 campaignId
     )
  ======================================================= */

  const hasParticipated =
    useCallback(
      async (
        wallet,
        campaignId
      ) =>
        executeRead((c) =>
          c.hasParticipated(
            ensureAddress(
              wallet,
              "wallet"
            ),
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getKindBalance
  ======================================================= */

  const getKindBalance =
    useCallback(
      async () =>
        executeRead((c) =>
          c.getKindBalance()
        ),
      [executeRead]
    );

  /* =======================================================
     getGlobalView
  ======================================================= */

  const getGlobalView =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getGlobalView(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getGlobalStats
  ======================================================= */

  const getGlobalStats =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getGlobalStats(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getActiveCampaignId
  ======================================================= */

  const getActiveCampaignId =
    useCallback(
      async () =>
        executeRead((c) =>
          c.getActiveCampaignId()
        ),
      [executeRead]
    );

  /* =======================================================
     getLeaderboard
     
     Solidity:
     getLeaderboard(
       campaignId,
       offset,
       limit
     )
     
     Returns:
     [
       entries,
       total
     ]
  ======================================================= */

  const getLeaderboard =
    useCallback(
      async (
        campaignId,
        offset,
        limit
      ) =>
        executeRead((c) =>
          c.getLeaderboard(
            campaignId,
            offset,
            limit
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getGlobalLeaderboard
  ======================================================= */

  const getGlobalLeaderboard =
    useCallback(
      async (
        campaignId,
        offset,
        limit
      ) =>
        executeRead((c) =>
          c.getGlobalLeaderboard(
            campaignId,
            offset,
            limit
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getLeaderboardTotal
  ======================================================= */

  const getLeaderboardTotal =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getLeaderboardTotal(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getGlobalLeaderboardTotal
  ======================================================= */

  const getGlobalLeaderboardTotal =
    useCallback(
      async (campaignId) =>
        executeRead((c) =>
          c.getGlobalLeaderboardTotal(
            campaignId
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getCampaignParticipant
  ======================================================= */

  const getCampaignParticipant =
    useCallback(
      async (
        campaignId,
        index
      ) =>
        executeRead((c) =>
          c.getCampaignParticipant(
            campaignId,
            index
          )
        ),
      [executeRead]
    );

  /* =======================================================
     getCampaignParticipants
  ======================================================= */

  const getCampaignParticipants =
    useCallback(
      async (
        campaignId,
        offset,
        limit
      ) =>
        executeRead((c) =>
          c.getCampaignParticipants(
            campaignId,
            offset,
            limit
          )
        ),
      [executeRead]
    );

  /* =======================================================
     isOperator
  ======================================================= */

  const isOperator =
    useCallback(
      async (accountAddress) =>
        executeRead((c) =>
          c.isOperator(
            ensureAddress(
              accountAddress,
              "account"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     isApprovedPair
  ======================================================= */

  const isApprovedPair =
    useCallback(
      async (pair) =>
        executeRead((c) =>
          c.isApprovedPair(
            ensureAddress(
              pair,
              "pair"
            )
          )
        ),
      [executeRead]
    );

  /* =======================================================
     defaultCampaignConfiguration
  ======================================================= */

  const getDefaultCampaignConfiguration =
    useCallback(
      async () =>
        executeRead((c) =>
          c.defaultCampaignConfiguration()
        ),
      [executeRead]
    );

  /* =======================================================
     FORMATTED HELPERS
     
     These DO NOT call additional Solidity functions.
     They only format values returned by the exact
     contract functions above.
  ======================================================= */

  const getKindUsdPriceFormatted =
    useCallback(
      async (campaignId) => {
        const value =
          await getKindUsdPrice(
            campaignId
          );

        return {
          raw: value,
          formatted: formatUsd(
            value
          ),
        };
      },
      [getKindUsdPrice]
    );

  const getWbnbUsdPriceFormatted =
    useCallback(
      async (campaignId) => {
        const value =
          await getWbnbUsdPrice(
            campaignId
          );

        return {
          raw: value,
          formatted: formatUsd(
            value
          ),
        };
      },
      [getWbnbUsdPrice]
    );

  const getNextClaimUsdFormatted =
    useCallback(
      async (
        campaignId,
        wallet
      ) => {
        const value =
          await getNextClaimUsd(
            campaignId,
            wallet
          );

        return {
          raw: value,
          formatted: formatUsd(
            value
          ),
        };
      },
      [getNextClaimUsd]
    );

  const getNextClaimKindFormatted =
    useCallback(
      async (
        campaignId,
        wallet,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => {
        const value =
          await getNextClaimKind(
            campaignId,
            wallet
          );

        return {
          raw: value,
          formatted:
            formatTokenAmount(
              value,
              kindDecimals
            ),
        };
      },
      [getNextClaimKind]
    );

  const getKindBalanceFormatted =
    useCallback(
      async (
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => {
        const value =
          await getKindBalance();

        return {
          raw: value,
          formatted:
            formatTokenAmount(
              value,
              kindDecimals
            ),
        };
      },
      [getKindBalance]
    );

  /* =======================================================
     NORMALIZE GLOBAL VIEW
     
     Keeps all exact contract fields.
     Only converts BigInt values into strings/numbers
     for convenient React rendering.
  ======================================================= */

  const getGlobalViewFormatted =
    useCallback(
      async (campaignId) => {
        const v =
          await getGlobalView(
            campaignId
          );

        return {
          raw: v,

          campaignId:
            toNumber(v.campaignId),

          totalParticipants:
            toNumber(
              v.totalParticipants
            ),

          totalQualified:
            toNumber(
              v.totalQualified
            ),

          totalCompleted:
            toNumber(
              v.totalCompleted
            ),

          totalDisqualified:
            toNumber(
              v.totalDisqualified
            ),

          totalVerifiedTrades:
            toNumber(
              v.totalVerifiedTrades
            ),

          totalKindBought:
            v.totalKindBought,

          totalKindBoughtFormatted:
            formatTokenAmount(
              v.totalKindBought
            ),

          totalTradeVolumeUsd:
            v.totalTradeVolumeUsd,

          totalTradeVolumeUsdFormatted:
            formatUsd(
              v.totalTradeVolumeUsd
            ),

          totalQualifyingUsd:
            v.totalQualifyingUsd,

          totalQualifyingUsdFormatted:
            formatUsd(
              v.totalQualifyingUsd
            ),

          totalRewardUsd:
            v.totalRewardUsd,

          totalRewardUsdFormatted:
            formatUsd(
              v.totalRewardUsd
            ),

          totalClaimableRewardUsd:
            v.totalClaimableRewardUsd,

          totalClaimableRewardUsdFormatted:
            formatUsd(
              v.totalClaimableRewardUsd
            ),

          totalClaimedKind:
            v.totalClaimedKind,

          totalClaimedKindFormatted:
            formatTokenAmount(
              v.totalClaimedKind
            ),

          poolKindBalance:
            v.poolKindBalance,

          poolKindBalanceFormatted:
            formatTokenAmount(
              v.poolKindBalance
            ),

          tier100Count:
            toNumber(
              v.tier100Count
            ),

          tier150Count:
            toNumber(
              v.tier150Count
            ),

          tier200Count:
            toNumber(
              v.tier200Count
            ),

          tier300Count:
            toNumber(
              v.tier300Count
            ),

          claimCount:
            toNumber(
              v.claimCount
            ),

          qualificationPeriod:
            toNumber(
              v.qualificationPeriod
            ),

          maxPurchaseUsd:
            v.maxPurchaseUsd,

          maxPurchaseUsdFormatted:
            formatUsd(
              v.maxPurchaseUsd
            ),

          startTime:
            toNumber(
              v.startTime
            ),

          endTime:
            toNumber(
              v.endTime
            ),

          claimEndTime:
            toNumber(
              v.claimEndTime
            ),

          activated:
            Boolean(v.activated),

          closed:
            Boolean(v.closed),
        };
      },
      [getGlobalView]
    );

  /* =======================================================
     NORMALIZE CAMPAIGN
  ======================================================= */

  const getCampaignFormatted =
    useCallback(
      async (campaignId) => {
        const c =
          await getCampaign(
            campaignId
          );

        return {
          raw: c,

          id:
            toNumber(c.id),

          startTime:
            toNumber(
              c.startTime
            ),

          endTime:
            toNumber(
              c.endTime
            ),

          claimEndTime:
            toNumber(
              c.claimEndTime
            ),

          qualificationPeriod:
            toNumber(
              c.qualificationPeriod
            ),

          minPurchaseUsd:
            c.minPurchaseUsd,

          minPurchaseUsdFormatted:
            formatUsd(
              c.minPurchaseUsd
            ),

          maxPurchaseUsd:
            c.maxPurchaseUsd,

          maxPurchaseUsdFormatted:
            formatUsd(
              c.maxPurchaseUsd
            ),

          claimCount:
            toNumber(
              c.claimCount
            ),

          activated:
            Boolean(c.activated),

          closed:
            Boolean(c.closed),

          kindToken:
            c.kindToken,

          usdtToken:
            c.usdtToken,

          wbnbToken:
            c.wbnbToken,

          kindWbnbPair:
            c.kindWbnbPair,

          wbnbUsdtPair:
            c.wbnbUsdtPair,

          totalParticipants:
            toNumber(
              c.totalParticipants
            ),

          totalQualified:
            toNumber(
              c.totalQualified
            ),

          totalCompleted:
            toNumber(
              c.totalCompleted
            ),

          totalDisqualified:
            toNumber(
              c.totalDisqualified
            ),

          totalTradeVolumeUsd:
            c.totalTradeVolumeUsd,

          totalTradeVolumeUsdFormatted:
            formatUsd(
              c.totalTradeVolumeUsd
            ),

          totalQualifyingUsd:
            c.totalQualifyingUsd,

          totalQualifyingUsdFormatted:
            formatUsd(
              c.totalQualifyingUsd
            ),

          totalKindBought:
            c.totalKindBought,

          totalKindBoughtFormatted:
            formatTokenAmount(
              c.totalKindBought
            ),

          totalVerifiedTrades:
            toNumber(
              c.totalVerifiedTrades
            ),

          totalRewardUsd:
            c.totalRewardUsd,

          totalRewardUsdFormatted:
            formatUsd(
              c.totalRewardUsd
            ),

          totalClaimableRewardUsd:
            c.totalClaimableRewardUsd,

          totalClaimableRewardUsdFormatted:
            formatUsd(
              c.totalClaimableRewardUsd
            ),

          totalClaimedKind:
            c.totalClaimedKind,

          totalClaimedKindFormatted:
            formatTokenAmount(
              c.totalClaimedKind
            ),

          tier100Count:
            toNumber(
              c.tier100Count
            ),

          tier150Count:
            toNumber(
              c.tier150Count
            ),

          tier200Count:
            toNumber(
              c.tier200Count
            ),

          tier300Count:
            toNumber(
              c.tier300Count
            ),
        };
      },
      [getCampaign]
    );

  /* =======================================================
     NORMALIZE USER STATS
  ======================================================= */

  const getUserStatsFormatted =
    useCallback(
      async (
        campaignId,
        wallet,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => {
        const s =
          await getUserStats(
            campaignId,
            wallet
          );

        return {
          raw: s,

          status:
            toNumber(
              s.status
            ),

          reason:
            toNumber(
              s.reason
            ),

          firstPurchaseTime:
            toNumber(
              s.firstPurchaseTime
            ),

          qualificationEnd:
            toNumber(
              s.qualificationEnd
            ),

          qualifyingPurchaseUsd:
            s.qualifyingPurchaseUsd,

          qualifyingPurchaseUsdFormatted:
            formatUsd(
              s.qualifyingPurchaseUsd
            ),

          frozenPurchaseUsd:
            s.frozenPurchaseUsd,

          frozenPurchaseUsdFormatted:
            formatUsd(
              s.frozenPurchaseUsd
            ),

          rewardBps:
            s.rewardBps,

          rewardBpsNumber:
            toNumber(
              s.rewardBps
            ),

          rewardPercent:
            toNumber(
              s.rewardBps
            ) / 100,

          rewardUsd:
            s.rewardUsd,

          rewardUsdFormatted:
            formatUsd(
              s.rewardUsd
            ),

          claimedKind:
            s.claimedKind,

          claimedKindFormatted:
            formatTokenAmount(
              s.claimedKind,
              kindDecimals
            ),

          claimsCompleted:
            toNumber(
              s.claimsCompleted
            ),

          claimCount:
            toNumber(
              s.claimCount
            ),

          tradeCount:
            toNumber(
              s.tradeCount
            ),

          nextClaimTime:
            toNumber(
              s.nextClaimTime
            ),

          currentKindBalance:
            s.currentKindBalance,

          currentKindBalanceFormatted:
            formatTokenAmount(
              s.currentKindBalance,
              kindDecimals
            ),
        };
      },
      [getUserStats]
    );

  /* =======================================================
     NORMALIZE CLAIM INFO
  ======================================================= */

  const getClaimInfoFormatted =
    useCallback(
      async (
        campaignId,
        wallet,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => {
        const info =
          await getClaimInfo(
            campaignId,
            wallet
          );

        return {
          raw: info,

          rewardUsd:
            info.rewardUsd,

          rewardUsdFormatted:
            formatUsd(
              info.rewardUsd
            ),

          kindPriceUsd:
            info.kindPriceUsd,

          kindPriceUsdFormatted:
            formatUsd(
              info.kindPriceUsd
            ),

          estimatedKind:
            info.estimatedKind,

          estimatedKindFormatted:
            formatTokenAmount(
              info.estimatedKind,
              kindDecimals
            ),

          claimedKind:
            info.claimedKind,

          claimedKindFormatted:
            formatTokenAmount(
              info.claimedKind,
              kindDecimals
            ),

          claimsCompleted:
            toNumber(
              info.claimsCompleted
            ),

          claimCount:
            toNumber(
              info.claimCount
            ),

          rewardBps:
            toNumber(
              info.rewardBps
            ),

          rewardPercent:
            toNumber(
              info.rewardBps
            ) / 100,

          qualificationEnd:
            toNumber(
              info.qualificationEnd
            ),

          qualifyingPurchaseUsd:
            info.qualifyingPurchaseUsd,

          qualifyingPurchaseUsdFormatted:
            formatUsd(
              info.qualifyingPurchaseUsd
            ),

          nextClaimTime:
            toNumber(
              info.nextClaimTime
            ),

          qualificationFinished:
            Boolean(
              info.qualificationFinished
            ),

          claimReady:
            Boolean(
              info.claimReady
            ),
        };
      },
      [getClaimInfo]
    );

  /* =======================================================
     NORMALIZE LEADERBOARD
  ======================================================= */

  const normalizeLeaderboardEntry =
    useCallback(
      (
        entry,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => ({
        raw: entry,

        wallet:
          entry.wallet,

        status:
          toNumber(
            entry.status
          ),

        reason:
          toNumber(
            entry.reason
          ),

        qualifyingPurchaseUsd:
          entry.qualifyingPurchaseUsd,

        qualifyingPurchaseUsdFormatted:
          formatUsd(
            entry.qualifyingPurchaseUsd
          ),

        frozenPurchaseUsd:
          entry.frozenPurchaseUsd,

        frozenPurchaseUsdFormatted:
          formatUsd(
            entry.frozenPurchaseUsd
          ),

        rewardBps:
          toNumber(
            entry.rewardBps
          ),

        rewardPercent:
          toNumber(
            entry.rewardBps
          ) / 100,

        rewardUsd:
          entry.rewardUsd,

        rewardUsdFormatted:
          formatUsd(
            entry.rewardUsd
          ),

        claimedKind:
          entry.claimedKind,

        claimedKindFormatted:
          formatTokenAmount(
            entry.claimedKind,
            kindDecimals
          ),

        claimsCompleted:
          toNumber(
            entry.claimsCompleted
          ),

        claimCount:
          toNumber(
            entry.claimCount
          ),

        tradeCount:
          toNumber(
            entry.tradeCount
          ),

        currentKindBalance:
          entry.currentKindBalance,

        currentKindBalanceFormatted:
          formatTokenAmount(
            entry.currentKindBalance,
            kindDecimals
          ),
      }),
      []
    );

  const getGlobalLeaderboardFormatted =
    useCallback(
      async (
        campaignId,
        offset,
        limit,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => {
        const result =
          await getGlobalLeaderboard(
            campaignId,
            offset,
            limit
          );

        const entries =
          result[0] || [];

        const total =
          result[1];

        const normalized =
          entries.map(
            (entry) =>
              normalizeLeaderboardEntry(
                entry,
                kindDecimals
              )
          );

        /*
         * IMPORTANT:
         *
         * Solidity does NOT sort.
         *
         * Your contract comments say frontend
         * should sort by qualifyingPurchaseUsd DESC.
         */
        normalized.sort(
          (
            a,
            b
          ) => {
            const aValue =
              a.qualifyingPurchaseUsd;

            const bValue =
              b.qualifyingPurchaseUsd;

            if (
              aValue > bValue
            ) {
              return -1;
            }

            if (
              aValue < bValue
            ) {
              return 1;
            }

            return 0;
          }
        );

        return {
          entries:
            normalized,
          total:
            toNumber(total),
          raw:
            result,
        };
      },
      [
        getGlobalLeaderboard,
        normalizeLeaderboardEntry,
      ]
    );

  /* =======================================================
     GET COMPLETE LEADERBOARD
     
     Uses ONLY:
     getGlobalLeaderboard()
     
     It simply pages through the exact contract API.
  ======================================================= */

  const getCompleteGlobalLeaderboard =
    useCallback(
      async (
        campaignId,
        pageSize = 50,
        kindDecimals = DEFAULT_KIND_DECIMALS
      ) => {
        const first =
          await getGlobalLeaderboard(
            campaignId,
            0,
            pageSize
          );

        const firstEntries =
          first[0] || [];

        const total =
          toNumber(first[1]);

        let allEntries =
          [...firstEntries];

        let offset =
          pageSize;

        while (
          offset < total
        ) {
          const page =
            await getGlobalLeaderboard(
              campaignId,
              offset,
              pageSize
            );

          const pageEntries =
            page[0] || [];

          allEntries =
            allEntries.concat(
              pageEntries
            );

          offset += pageSize;

          if (
            pageEntries.length === 0
          ) {
            break;
          }
        }

        const normalized =
          allEntries.map(
            (entry) =>
              normalizeLeaderboardEntry(
                entry,
                kindDecimals
              )
          );

        normalized.sort(
          (
            a,
            b
          ) => {
            if (
              a.qualifyingPurchaseUsd >
              b.qualifyingPurchaseUsd
            ) {
              return -1;
            }

            if (
              a.qualifyingPurchaseUsd <
              b.qualifyingPurchaseUsd
            ) {
              return 1;
            }

            return 0;
          }
        );

        return {
          entries:
            normalized,
          total,
        };
      },
      [
        getGlobalLeaderboard,
        normalizeLeaderboardEntry,
      ]
    );

  /* =======================================================
     RETURN
  ======================================================= */

  return {
    /* -----------------------------------------------
       CONNECTION
    ----------------------------------------------- */

    account,
    isConnected,
    chainId,

    provider,
    signer,

    contract,
    readContract,

    contractAddress:
      CONTRACT_ADDRESS,

    loading,
    error,
    lastTxHash,

    /* -----------------------------------------------
       RAW CONVERSION HELPERS
    ----------------------------------------------- */

    usdToWei,
    tokenToUnits,

    formatTokenAmount,
    formatUsd,

    /* -----------------------------------------------
       ENUMS
    ----------------------------------------------- */

    WALLET_STATUS,
    DISQUALIFICATION_REASON,

    /* -----------------------------------------------
       CONSTANTS
    ----------------------------------------------- */

    getBps,
    getUsdOne,
    getClaimCooldown,
    getDefaultQualificationPeriod,
    getDefaultMaxPurchaseUsd,
    getMaxClaims,

    getReward100,
    getReward150,
    getReward200,
    getReward300,

    getNextCampaignId,
    getDefaultKindToken,

    /* -----------------------------------------------
       ADMIN READS
    ----------------------------------------------- */

    isApprovedKindPair,
    isAuthorizedOperator,
    isTradeRecorded,

    /* -----------------------------------------------
       ADMIN WRITES
    ----------------------------------------------- */

    setOperator,
    setApprovedKindPair,

    createCampaign,
    createCampaignFromValues,

    activateCampaign,

    recordVerifiedBuy,
    recordVerifiedBuyFromValues,

    disqualifyWallet,
    emergencyDisqualify,

    pause,
    unpause,

    closeCampaign,

    fundRewardContract,
    fundRewardContractFromValue,

    withdrawExcessKind,
    withdrawExcessKindFromValue,

    withdrawERC20,
    withdrawERC20FromValue,

    /* -----------------------------------------------
       USER CLAIM
    ----------------------------------------------- */

    claim,

    /* -----------------------------------------------
       PRICE
    ----------------------------------------------- */

    getWbnbUsdPrice,
    getKindUsdPrice,

    getWbnbUsdPriceFormatted,
    getKindUsdPriceFormatted,

    /* -----------------------------------------------
       USER
    ----------------------------------------------- */

    getUserStats,
    getUserStatsFormatted,

    getParticipant,

    getWalletStatus,
    hasParticipated,

    /* -----------------------------------------------
       CAMPAIGN
    ----------------------------------------------- */

    getCampaign,
    getCampaignFormatted,

    getActiveCampaignId,

    getRewardBpsForAmount,
    getRewardBpsForUsd,

    /* -----------------------------------------------
       CLAIM INFO
    ----------------------------------------------- */

    getNextClaimUsd,
    getNextClaimUsdFormatted,

    getNextClaimKind,
    getNextClaimKindFormatted,

    getClaimInfo,
    getClaimInfoFormatted,

    isClaimReady,

    getTimeUntilNextClaim,

    /* -----------------------------------------------
       TRADES
    ----------------------------------------------- */

    getTrade,
    getTradeCount,

    /* -----------------------------------------------
       POOL
    ----------------------------------------------- */

    getKindBalance,
    getKindBalanceFormatted,

    /* -----------------------------------------------
       GLOBAL
    ----------------------------------------------- */

    getGlobalView,
    getGlobalViewFormatted,

    getGlobalStats,

    /* -----------------------------------------------
       LEADERBOARD
    ----------------------------------------------- */

    getLeaderboard,

    getGlobalLeaderboard,
    getGlobalLeaderboardFormatted,

    getCompleteGlobalLeaderboard,

    getLeaderboardTotal,
    getGlobalLeaderboardTotal,

    getCampaignParticipant,
    getCampaignParticipants,

    /* -----------------------------------------------
       ADMIN HELPERS
    ----------------------------------------------- */

    isOperator,
    isApprovedPair,

    getDefaultCampaignConfiguration,

    /* -----------------------------------------------
       ERROR
    ----------------------------------------------- */

    clearError: () =>
      setError(null),
  };
}